import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, Save, Settings2 } from "lucide-react";
import { Api } from "../lib/api.js";
import { getActiveProfileId } from "../lib/profile.js";
import {
  DEFAULT_CAROUSEL,
  HOME_SECTIONS,
  type CarouselSettings,
  type HomeSectionDefinition,
} from "../lib/homeLayout.js";

export function Preferences(): JSX.Element {
  const profileId = getActiveProfileId();
  const profilesQ = useQuery({ queryKey: ["profiles"], queryFn: Api.profiles.list });
  const profile = profilesQ.data?.profiles.find((p) => p.id === profileId);

  const prefsQ = useQuery({
    queryKey: ["preferences", profileId],
    queryFn: () => Api.onboarding.preferences(profileId!),
    enabled: !!profileId,
  });

  const tags = useMemo(() => {
    if (!prefsQ.data?.preferences.tag_weights) return [];
    return Object.entries(prefsQ.data.preferences.tag_weights)
      .map(([k, v]) => ({ key: k, weight: v }))
      .sort((a, b) => b.weight - a.weight);
  }, [prefsQ.data]);

  const creators = useMemo(() => {
    if (!prefsQ.data?.preferences.creator_weights) return [];
    return Object.entries(prefsQ.data.preferences.creator_weights)
      .map(([k, v]) => ({ key: k, weight: v }))
      .sort((a, b) => b.weight - a.weight);
  }, [prefsQ.data]);

  const durationByTag = useMemo(() => {
    if (!prefsQ.data?.preferences.duration_pref_by_tag) return [];
    return Object.entries(prefsQ.data.preferences.duration_pref_by_tag) as [
      string,
      { mean_s?: number; std_s?: number; n?: number },
    ][];
  }, [prefsQ.data]);

  if (!profileId) {
    return (
      <div>
        <h1 className="font-display text-3xl text-ivory">Preferences</h1>
        <div className="ornament-rule" />
        <div className="card p-8 text-parchment">Pick a profile first.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl text-ivory">Preferences</h1>
        {profile && (
          <span className="section-label">{profile.display_name}</span>
        )}
      </div>
      <div className="ornament-rule" />

      {prefsQ.isLoading ? (
        <div className="space-y-2"><div className="skeleton h-48" /><div className="skeleton h-48" /></div>
      ) : !prefsQ.data ? (
        <div className="card p-8 text-parchment text-center">
          No preference vector yet. Complete onboarding (or watch a few things and rate them) and refresh.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PreferenceBars
              label="Tags & topics — pulling toward / away"
              rows={tags}
              max={20}
            />
            <PreferenceBars
              label="Creators — your YouTube taste"
              rows={creators}
              max={20}
              positiveOnly
            />
          </div>

          {durationByTag.length > 0 && (
            <section className="mt-10">
              <div className="section-label mb-3">Duration sweet spots by tag</div>
              <ul className="card p-5 space-y-2">
                {durationByTag.map(([tag, v]) => (
                  <li key={tag} className="grid grid-cols-[140px_1fr_120px] gap-4 items-center text-sm">
                    <span className="text-ivory font-mono">{tag}</span>
                    <span className="text-parchment text-xs tabular-nums">
                      mean {fmtSeconds(v.mean_s)} · σ {fmtSeconds(v.std_s)}
                    </span>
                    <span className="text-parchment text-xs text-right">n = {v.n ?? 0}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-10">
            <div className="section-label mb-3">Summary</div>
            <div className="card p-5 grid grid-cols-3 gap-6 text-center">
              <Stat label="Tag dimensions" value={tags.length} />
              <Stat label="Creators tracked" value={creators.length} />
              <Stat label="Updated" value={new Date(prefsQ.data.preferences.updated_at).toLocaleString()} small />
            </div>
          </section>

          <HomeLayoutEditor profileId={profileId} />
          <ScreensaverEditor />
        </>
      )}
    </div>
  );
}

function PreferenceBars({
  label,
  rows,
  max,
  positiveOnly = false,
}: {
  label: string;
  rows: { key: string; weight: number }[];
  max: number;
  positiveOnly?: boolean;
}): JSX.Element {
  const positives = rows.filter((r) => r.weight > 0).slice(0, max);
  const negatives = positiveOnly ? [] : rows.filter((r) => r.weight < 0).slice(-Math.min(max / 2, rows.length));

  return (
    <div>
      <div className="section-label mb-3">{label}</div>
      <div className="card p-5 space-y-4">
        {positives.length > 0 && (
          <div>
            <div className="text-goldHi text-[11px] font-display tracking-smallcaps mb-2">PULLING TOWARD</div>
            <ul className="space-y-1">
              {positives.map((r) => <Bar key={r.key} label={r.key} weight={r.weight} positive />)}
            </ul>
          </div>
        )}
        {negatives.length > 0 && (
          <div>
            <div className="text-vermilionHi text-[11px] font-display tracking-smallcaps mb-2 mt-4">PULLING AWAY</div>
            <ul className="space-y-1">
              {negatives.map((r) => <Bar key={r.key} label={r.key} weight={r.weight} />)}
            </ul>
          </div>
        )}
        {positives.length === 0 && negatives.length === 0 && (
          <div className="text-parchment text-sm">No weighted entries yet.</div>
        )}
      </div>
    </div>
  );
}

function Bar({ label, weight, positive }: { label: string; weight: number; positive?: boolean }): JSX.Element {
  const pct = Math.min(100, Math.round(Math.abs(weight) * 100));
  const color = positive ? "#C7A24A" : "#A4202D";
  return (
    <li className="grid grid-cols-[140px_1fr_60px] gap-3 items-center">
      <span className="text-ivory font-mono text-xs truncate" title={label}>{label}</span>
      <div className="h-2 bg-hairline rounded overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-parchment text-xs tabular-nums text-right">
        {weight > 0 ? "+" : ""}{weight.toFixed(2)}
      </span>
    </li>
  );
}

function Stat({ label, value, small }: { label: string; value: string | number; small?: boolean }): JSX.Element {
  return (
    <div>
      <div className="section-label">{label}</div>
      <div className={small ? "text-ivory text-xs mt-2" : "text-goldHi text-2xl font-display tabular-nums mt-2"}>
        {value}
      </div>
    </div>
  );
}

function fmtSeconds(s: number | undefined): string {
  if (!s) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

/** Merge the fixed Home rows with the current recommendation response so
 * dynamic kinds (especially genre_*) cannot drift out of the editor. */
function homeSectionCatalog(sections: { kind: string; label: string }[]): HomeSectionDefinition[] {
  const catalog = new Map(HOME_SECTIONS.map((s) => [s.kind, s]));
  for (const section of sections) {
    if (!catalog.has(section.kind)) {
      catalog.set(section.kind, {
        kind: section.kind,
        label: section.label,
        hint: "Current recommendation carousel",
      });
    }
  }
  return [...catalog.values()];
}

interface HomeLayoutState {
  order: string[];
  hidden: string[];
  carouselSettings: Record<string, CarouselSettings>;
}

function HomeLayoutEditor({ profileId }: { profileId: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["home-layout", profileId],
    queryFn: () => Api.profiles.getHomeLayout(profileId),
  });
  const recsQ = useQuery({
    queryKey: ["recs", profileId],
    queryFn: () => Api.recommendations(profileId),
  });
  const catalog = useMemo(
    () => homeSectionCatalog(recsQ.data?.sections ?? []),
    [recsQ.data],
  );
  const [state, setState] = useState<HomeLayoutState | null>(null);
  const [dirty, setDirty] = useState(false);

  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data || !recsQ.data) return;
    const layout = q.data.layout;
    const serverOrder = layout.order ?? [];
    const serverHidden = layout.hidden ?? [];
    const known = new Set(catalog.map((s) => s.kind));
    const order = [
      ...serverOrder.filter((k) => known.has(k)),
      ...catalog.map((s) => s.kind).filter((k) => !serverOrder.includes(k)),
    ];
    setState({ order, hidden: serverHidden.filter((k) => known.has(k)), carouselSettings: layout.carouselSettings ?? {} });
    setDirty(false);
  }, [q.data, recsQ.data, catalog]);

  const save = useMutation({
    mutationFn: () => Api.profiles.putHomeLayout(profileId, state!),
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ["home-layout", profileId] });
      void qc.invalidateQueries({ queryKey: ["recs", profileId] });
    },
  });

  function move(idx: number, dir: -1 | 1): void {
    if (!state) return;
    const ni = idx + dir;
    if (ni < 0 || ni >= state.order.length) return;
    const next = [...state.order];
    [next[idx], next[ni]] = [next[ni]!, next[idx]!];
    setState({ ...state, order: next });
    setDirty(true);
  }
  function toggleHidden(kind: string): void {
    if (!state) return;
    const hidden = state.hidden.includes(kind)
      ? state.hidden.filter((k) => k !== kind)
      : [...state.hidden, kind];
    setState({ ...state, hidden });
    setDirty(true);
  }
  function reset(): void {
    setState({ order: catalog.map((s) => s.kind), hidden: [], carouselSettings: {} });
    setDirty(true);
  }

  function setCarouselSetting<K extends keyof CarouselSettings>(kind: string, key: K, value: CarouselSettings[K]): void {
    if (!state) return;
    const prev = state.carouselSettings[kind] ?? DEFAULT_CAROUSEL;
    setState({ ...state, carouselSettings: { ...state.carouselSettings, [kind]: { ...prev, [key]: value } } });
    setDirty(true);
  }

  function carouselSetting<K extends keyof CarouselSettings>(kind: string, key: K): CarouselSettings[K] {
    return (state?.carouselSettings[kind] ?? DEFAULT_CAROUSEL)[key];
  }

  if (!state) return <div className="skeleton h-48 mt-10" />;

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between">
        <div className="section-label">Home layout</div>
        <div className="flex gap-2">
          <button onClick={reset} className="pill-ghost" title="Restore default order & show everything">
            <RotateCcw size={14} /> Reset
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="pill"
          >
            <Save size={14} /> {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <p className="text-parchment text-sm mt-2 max-w-[60ch]">
        Reorder the carousels that appear on your Home page, or hide ones you
        don't want. Changes apply just to this profile.
      </p>
      <ul className="card p-2 mt-4 space-y-1">
        {state.order.map((kind, idx) => {
          const meta = catalog.find((s) => s.kind === kind);
          if (!meta) return null;
          const hidden = state.hidden.includes(kind);
          const isExpanded = expanded === kind;
          const count = carouselSetting(kind, "count");
          const refreshPct = carouselSetting(kind, "refreshPct");
          const refreshMinutes = carouselSetting(kind, "refreshMinutes");
          return (
            <li key={kind} className={`rounded-md ${hidden ? "opacity-50" : ""}`}>
              {/* Main row */}
              <div className="flex items-center gap-3 px-3 py-2 hover:bg-elev/40 rounded-md">
                <div className="flex flex-col gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="text-parchment hover:text-gold disabled:opacity-20 disabled:cursor-not-allowed" aria-label="Move up">
                    <ArrowUp size={14} />
                  </button>
                  <button onClick={() => move(idx, 1)} disabled={idx === state.order.length - 1}
                    className="text-parchment hover:text-gold disabled:opacity-20 disabled:cursor-not-allowed" aria-label="Move down">
                    <ArrowDown size={14} />
                  </button>
                </div>
                <span className="w-7 text-parchment/60 text-xs tabular-nums text-center">{idx + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-ivory font-display tracking-smallcaps truncate">{meta.label}</div>
                  <div className="text-parchment/60 text-xs truncate">
                    {count} cards · refresh {refreshPct}% every {refreshMinutes >= 60 ? `${refreshMinutes / 60}h` : `${refreshMinutes}m`}
                  </div>
                </div>
                <button onClick={() => setExpanded(isExpanded ? null : kind)}
                  className={`pill-ghost ${isExpanded ? "text-gold" : ""}`} title="Carousel settings">
                  <Settings2 size={14} />
                </button>
                <button onClick={() => toggleHidden(kind)}
                  className={`pill-ghost ${hidden ? "text-vermilionHi" : "text-jade"}`}
                  title={hidden ? "Show" : "Hide"}>
                  {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {/* Expandable settings panel */}
              {isExpanded && (
                <div className="mx-3 mb-2 px-4 py-3 bg-elev/30 rounded-md grid grid-cols-3 gap-4">
                  <label className="flex flex-col gap-1">
                    <span className="text-parchment/60 text-xs uppercase tracking-widest">Cards</span>
                    <input type="number" min={1} max={40} value={count}
                      onChange={e => setCarouselSetting(kind, "count", Math.max(1, Math.min(40, Number(e.target.value))))}
                      className="bg-transparent border border-hairline rounded px-2 py-1 text-ivory text-sm w-20" />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-parchment/60 text-xs uppercase tracking-widest">Rotate %</span>
                    <input type="number" min={1} max={100} value={refreshPct}
                      onChange={e => setCarouselSetting(kind, "refreshPct", Math.max(1, Math.min(100, Number(e.target.value))))}
                      className="bg-transparent border border-hairline rounded px-2 py-1 text-ivory text-sm w-20" />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-parchment/60 text-xs uppercase tracking-widest">Every (min)</span>
                    <input type="number" min={1} max={1440} value={refreshMinutes}
                      onChange={e => setCarouselSetting(kind, "refreshMinutes", Math.max(1, Math.min(1440, Number(e.target.value))))}
                      className="bg-transparent border border-hairline rounded px-2 py-1 text-ivory text-sm w-24" />
                  </label>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {save.isError && (
        <div className="text-vermilionHi text-sm mt-2">{(save.error as Error).message}</div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Screensaver settings — Flying Toasters config (speed, doneness, density).
// ---------------------------------------------------------------------------

const SPEED_LABELS: { value: string; label: string }[] = [
  { value: "snail",     label: "Snail" },
  { value: "slow",      label: "Slow" },
  { value: "medium",    label: "Medium" },
  { value: "fast",      label: "Fast" },
  { value: "lightning", label: "Lightning" },
];
const DONENESS_LABELS: { value: string; label: string }[] = [
  { value: "light",  label: "Light" },
  { value: "golden", label: "Golden" },
  { value: "dark",   label: "Dark" },
  { value: "burnt",  label: "Burnt" },
];

function ScreensaverEditor(): JSX.Element {
  const [speed,    setSpeed]    = useState("medium");
  const [doneness, setDoneness] = useState("golden");
  const [density,  setDensity]  = useState(6);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const dirty = useRef(false);

  useEffect(() => {
    Api.screensaver.get()
      .then(s => { setSpeed(s.speed); setDoneness(s.doneness); setDensity(s.density); })
      .catch(() => undefined);
  }, []);

  async function save(): Promise<void> {
    setSaving(true);
    try {
      await Api.screensaver.save({ speed, doneness, density });
      setSaved(true);
      dirty.current = false;
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  function mark<T>(setter: (v: T) => void): (v: T) => void {
    return (v: T) => { setter(v); dirty.current = true; setSaved(false); };
  }

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between">
        <div className="section-label">Flying Toasters screensaver</div>
        <button onClick={save} disabled={saving} className="pill">
          <Save size={14} /> {saving ? "Saving…" : saved ? "Saved ✓" : "Save"}
        </button>
      </div>
      <p className="text-parchment text-sm mt-2 max-w-[60ch]">
        Controls the TV screen while idle. Changes apply on the TV within 30 seconds.
      </p>
      <div className="card p-5 mt-4 space-y-6">
        {/* Speed */}
        <div>
          <div className="text-ivory text-sm font-display tracking-smallcaps mb-3">Speed</div>
          <div className="flex gap-2 flex-wrap">
            {SPEED_LABELS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => mark(setSpeed)(value)}
                className={`pill-ghost ${speed === value ? "bg-gold/20 text-gold border-gold/50" : ""}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* Doneness */}
        <div>
          <div className="text-ivory text-sm font-display tracking-smallcaps mb-3">Toast doneness</div>
          <div className="flex gap-2 flex-wrap">
            {DONENESS_LABELS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => mark(setDoneness)(value)}
                className={`pill-ghost ${doneness === value ? "bg-gold/20 text-gold border-gold/50" : ""}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* Density */}
        <div>
          <div className="text-ivory text-sm font-display tracking-smallcaps mb-3">
            Density — {density} flyer{density !== 1 ? "s" : ""}
          </div>
          <input
            type="range" min={1} max={20} value={density}
            onChange={e => mark(setDensity)(Number(e.target.value))}
            className="w-full max-w-xs accent-gold"
          />
          <div className="flex justify-between text-parchment/50 text-xs max-w-xs mt-1">
            <span>1</span><span>20</span>
          </div>
        </div>
      </div>
    </section>
  );
}
